import { computed, type ComputedRef, type Ref } from 'vue';
import { useStore } from 'vuex';
import { storeToRefs } from 'pinia';
import { useShipmentFormStore } from '@/stores/useShipmentFormStore';
import { useShipmentFormSave } from '@/composables/useShipmentFormSave';
import {
  emptyGeneralErrors,
  useShipmentFormValidation,
  type ShipmentFormGeneralErrors,
} from '@/composables/useShipmentFormValidation';
import type { ShipmentFormStopsPort } from '@/composables/shipment-form-stops-port';
import type { RoutingStrategy } from '@/components/shipment/form/utils/shipment-form-routing';

export function useShipmentFormFlowBase(options: {
  stopsPort: Ref<ShipmentFormStopsPort | null>;
  generalErrors: Ref<ShipmentFormGeneralErrors>;
  routingStrategy?: Ref<RoutingStrategy> | ComputedRef<RoutingStrategy>;
  skipRoutePersistWhenServerRoutesExist?: Ref<boolean> | ComputedRef<boolean>;
  deviceIds?: Ref<readonly number[]> | ComputedRef<readonly number[]>;
}) {
  const vuexStore = useStore();
  const accountId = computed(
    () => vuexStore.getters['auth/accountId'] as number,
  );

  const formStore = useShipmentFormStore();
  const { shipmentId, serverRoutesEmptyAtHydrate, selectedDeviceIds } =
    storeToRefs(formStore);

  const validation = useShipmentFormValidation({
    generalErrors: options.generalErrors,
  });

  const formSave = useShipmentFormSave({
    accountId,
    shipmentId,
    stopsPort: options.stopsPort,
    routingStrategy: options.routingStrategy,
    skipRoutePersistWhenServerRoutesExist:
      options.skipRoutePersistWhenServerRoutesExist ??
      serverRoutesEmptyAtHydrate,
    deviceIds: options.deviceIds,
  });

  return {
    vuexStore,
    accountId,
    formStore,
    shipmentId,
    validation,
    formSave,
    emptyGeneralErrors,
  };
}
